import { LineChartOutlined } from '@ant-design/icons'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useNavigate } from 'react-router-dom'

import type {
  DropProductMovement,
  LoadProductMovement,
  RouteLocation,
  RouteLogResponse,
} from '../../../../api/useFreight/useOrderRouteLog'
import { formatAddress } from '../utils'
import { LidCodeIcon } from './LidCodeIcon'
import { VolumeCircle } from './VolumeCircle'
import { useSiteMonitoring } from '@/api/useSiteMonitoring'

type InvoiceDropSummaryProps = {
  routeLogData: RouteLogResponse | undefined
}

export function InvoiceDropSummary({ routeLogData }: InvoiceDropSummaryProps): JSX.Element | null {
  if (!routeLogData) return null

  const { useSitesByIdsQuery } = useSiteMonitoring()

  const drops = routeLogData?.route?.filter((route) => route.action === 'drop')
  const siteIds = drops.map((drop) => drop.site_id)
  const siteQueries = useSitesByIdsQuery(siteIds.filter((id): id is string => !!id))
  const sitesTankData = siteQueries.reduce(
    (acc, query) => {
      if (query.data) {
        acc[query.data.id] = query.data.tanks
      }
      return acc
    },
    {} as Record<string, Gravitate.Tank[]>
  )
  return (
    <Vertical flex={1} style={{ borderTop: '1px solid var(--gray-300)' }} gap={8} scroll>
      <Texto className='p-2' category='p2'>
        Drop Summary
      </Texto>
      {drops?.map((drop) => (
        <Vertical
          flex={'none'}
          height={'auto'}
          key={drop.location_name}
          style={{ overflow: 'visible' }}
          className='px-4'
        >
          <Horizontal verticalCenter justifyContent='space-between' style={{ overflow: 'visible' }}>
            <Texto weight={'bold'} category='h5'>
              {drop.location_name}
            </Texto>
            <Texto align='right'>{formatAddress(drop)}</Texto>
          </Horizontal>
          <Vertical gap={8} width={'100%'}>
            {drop.product_movements.map((movement) => (
              <DropoffItem
                detail={movement}
                drop={drop}
                key={`${movement.product_name}-${movement.tank_id}`}
                sitesTankData={sitesTankData}
              />
            ))}
          </Vertical>
        </Vertical>
      ))}
    </Vertical>
  )
}

type DropoffItemProps = {
  detail: DropProductMovement | LoadProductMovement
  drop: RouteLocation
  sitesTankData: Record<string, Gravitate.Tank[]>
}
function DropoffItem({ detail, drop, sitesTankData }: DropoffItemProps): JSX.Element {
  const beforeDropVolume = detail.before_measurement
  const afterVolume = detail.after_measurement
  const lidCodeUrl = detail.lid_code_url
  const tankVolume = sitesTankData[drop.site_id || '']?.find(
    (tank) => String(tank.tank_id) === String(detail.tank_id)
  )?.tank_size
  const navigate = useNavigate()

  return (
    <Horizontal gap={24} verticalCenter style={{ overflow: 'visible' }}>
      <Horizontal gap={8} verticalCenter style={{ minWidth: '80px' }}>
        <LidCodeIcon showBackground={false} lidUrl={lidCodeUrl || ''} productName={detail.product_name} />
        {drop.site_id && (
          <GraviButton
            icon={<LineChartOutlined />}
            onClick={() => {
              navigate(`/site_monitoring/${drop.site_id}`)
            }}
            variant='text'
            size='small'
            style={{ minWidth: 'auto', padding: '2px 4px' }}
          />
        )}
        {beforeDropVolume && (
          <VolumeCircle beforeVolume={beforeDropVolume} afterVolume={afterVolume} size={24} tankVolume={tankVolume} />
        )}
      </Horizontal>

      <Vertical>
        <Texto category='label' appearance='hint' weight='600' style={{ gap: 4 }}>
          Tank ID: {detail.tank_id}
        </Texto>
        <Texto>{detail.product_name}</Texto>
      </Vertical>
      <Vertical>
        <Texto category='label' style={{ gap: 4 }} align='right'>
          Drop Vol.
        </Texto>
        <Texto category='p2' weight='bold' style={{ textAlign: 'right' }}>
          {addCommasToNumber(detail.dropped_volume, 0)}
        </Texto>{' '}
      </Vertical>
    </Horizontal>
  )
}
